import { marked } from 'marked';
import { apiClient } from '@/app/api/client';
import { geoUtils } from '@/utils/geoUtils';

export interface WordPressConnection {
  id: string;
  settings: {
    siteUrl: string;        // e.g., https://myblog.com
    username: string;       // WordPress username
    appPassword: string;    // Application Password (WP 5.6+)
    // Optional settings:
    defaultCategory?: number;
    defaultStatus?: 'publish' | 'draft' | 'pending';
  };
}

export interface WordPressPost {
  title: string;
  content: string;
  tags?: string[];
  categories?: string[];
  featuredImage?: string; // URL to image
  excerpt?: string;
  status?: 'publish' | 'draft' | 'pending';
  authorSettings?: {
    jobTitle?: string;
    bio?: string;
    socialLinks?: {
      linkedin?: string;
      twitter?: string;
    };
  };
}

export interface PublishingProgress {
  connectionId: string;
  platformName: string;
  status: 'pending' | 'in_progress' | 'success' | 'error';
  message?: string;
  externalUrl?: string;
}

class WordPressPublishingService {
  /**
   * Convert markdown to WordPress-friendly HTML
   */
  private convertMarkdownToHtml(markdown: string): string {
    if (!markdown) return '';

    // Configure marked for WordPress-friendly output
    marked.setOptions({
      breaks: true,        // Convert line breaks to <br>
      gfm: true           // GitHub Flavored Markdown
    });

    let html = marked.parse(markdown) as string;

    // Inject inline styles into table elements for external platform compatibility
    html = this.styleTablesForExternalPlatform(html);

    return html;
  }

  /**
   * Inject inline styles into table HTML elements so tables render with
   * borders, padding, and header styling on external platforms.
   */
  private styleTablesForExternalPlatform(html: string): string {
    return html
      .replace(/<table>/gi,
        '<table style="border-collapse: collapse; width: 100%; margin: 1.5em 0; font-size: 0.95em;">')
      .replace(/<th>/gi,
        '<th style="border: 1px solid #d1d5db; padding: 10px 14px; background-color: #f3f4f6; font-weight: 600; text-align: left;">')
      .replace(/<th /gi,
        '<th style="border: 1px solid #d1d5db; padding: 10px 14px; background-color: #f3f4f6; font-weight: 600; text-align: left;" ')
      .replace(/<td>/gi,
        '<td style="border: 1px solid #d1d5db; padding: 10px 14px;">')
      .replace(/<td /gi,
        '<td style="border: 1px solid #d1d5db; padding: 10px 14px;" ');
  }

  /**
   * Upload image to WordPress media library
   */
  private async uploadImage(
    connection: WordPressConnection,
    imageUrl: string,
    filename: string
  ): Promise<number> {
    const { siteUrl, username, appPassword } = connection.settings;

    try {
      // Fetch the image
      const imageResponse = await fetch(imageUrl);
      if (!imageResponse.ok) {
        throw new Error(`Failed to fetch image: ${imageResponse.statusText}`);
      }

      const imageBlob = await imageResponse.blob();

      // Upload to WordPress media library
      const formData = new FormData();
      formData.append('file', imageBlob, filename);

      const response = await fetch(`${siteUrl}/wp-json/wp/v2/media`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${btoa(`${username}:${appPassword}`)}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Failed to upload image: ${errorData.message || response.statusText}`);
      }

      const media = await response.json();
      return media.id;
    } catch (error) {
      console.error('Error uploading image to WordPress:', error);
      throw error;
    }
  }

  /**
   * Get or create category by name
   */
  private async getCategoryId(
    connection: WordPressConnection,
    categoryName: string
  ): Promise<number> {
    const { siteUrl, username, appPassword } = connection.settings;

    try {
      // Search for existing category
      const searchResponse = await fetch(
        `${siteUrl}/wp-json/wp/v2/categories?search=${encodeURIComponent(categoryName)}`,
        {
          headers: {
            'Authorization': `Basic ${btoa(`${username}:${appPassword}`)}`
          }
        }
      );

      if (searchResponse.ok) {
        const categories = await searchResponse.json();
        if (categories.length > 0) {
          return categories[0].id;
        }
      }

      // Create new category if not found
      const createResponse = await fetch(`${siteUrl}/wp-json/wp/v2/categories`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${btoa(`${username}:${appPassword}`)}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: categoryName })
      });

      if (!createResponse.ok) {
        throw new Error(`Failed to create category: ${createResponse.statusText}`);
      }

      const newCategory = await createResponse.json();
      return newCategory.id;
    } catch (error) {
      console.error('Error getting/creating category:', error);
      throw error;
    }
  }

  /**
   * Get or create tags by name
   */
  private async getTagIds(
    connection: WordPressConnection,
    tagNames: string[]
  ): Promise<number[]> {
    const { siteUrl, username, appPassword } = connection.settings;
    const tagIds: number[] = [];

    for (const tagName of tagNames) {
      try {
        // Search for existing tag
        const searchResponse = await fetch(
          `${siteUrl}/wp-json/wp/v2/tags?search=${encodeURIComponent(tagName)}`,
          {
            headers: {
              'Authorization': `Basic ${btoa(`${username}:${appPassword}`)}`
            }
          }
        );

        if (searchResponse.ok) {
          const tags = await searchResponse.json();
          if (tags.length > 0) {
            tagIds.push(tags[0].id);
            continue;
          }
        }

        // Create new tag if not found
        const createResponse = await fetch(`${siteUrl}/wp-json/wp/v2/tags`, {
          method: 'POST',
          headers: {
            'Authorization': `Basic ${btoa(`${username}:${appPassword}`)}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ name: tagName })
        });

        if (createResponse.ok) {
          const newTag = await createResponse.json();
          tagIds.push(newTag.id);
        }
      } catch (error) {
        console.error(`Error processing tag "${tagName}":`, error);
        // Continue with other tags even if one fails
      }
    }

    return tagIds;
  }

  /**
   * Publish article to WordPress
   */
  async publishToWordPress(
    connection: WordPressConnection,
    post: WordPressPost,
    onProgress: (progress: PublishingProgress) => void
  ): Promise<string> {
    const { siteUrl, username, appPassword } = connection.settings;

    try {
      onProgress({
        connectionId: connection.id,
        platformName: 'WordPress',
        status: 'in_progress',
        message: 'Converting content to HTML...'
      });

      let htmlContent = this.convertMarkdownToHtml(post.content);

      // Inject JSON-LD Schema (Invisible)
      const schema = geoUtils.generateArticleSchema({
        title: post.title,
        summary: post.excerpt || '',
        authorName: 'Content Growth User',
        authorJobTitle: post.authorSettings?.jobTitle,
        authorBio: post.authorSettings?.bio,
        authorSocialLinks: post.authorSettings?.socialLinks ? Object.values(post.authorSettings.socialLinks).filter(Boolean) as string[] : undefined,
        canonicalUrl: '', // WP generates its own URL
      });
      htmlContent += schema;

      // Inject Visible Summary if excerpt exists
      if (post.excerpt) {
        htmlContent = geoUtils.generateVisibleSummary(post.excerpt) + htmlContent;
      }

      // Handle categories
      const categoryIds: number[] = [];
      if (post.categories && post.categories.length > 0) {
        onProgress({
          connectionId: connection.id,
          platformName: 'WordPress',
          status: 'in_progress',
          message: 'Processing categories...'
        });

        for (const cat of post.categories) {
          const catId = await this.getCategoryId(connection, cat);
          categoryIds.push(catId);
        }
      } else if (connection.settings.defaultCategory) {
        categoryIds.push(connection.settings.defaultCategory);
      }

      // Handle tags
      let tagIds: number[] = [];
      if (post.tags && post.tags.length > 0) {
        onProgress({
          connectionId: connection.id,
          platformName: 'WordPress',
          status: 'in_progress',
          message: 'Processing tags...'
        });

        tagIds = await this.getTagIds(connection, post.tags);
      }

      // Handle featured image if provided
      let featuredMediaId: number | undefined;
      if (post.featuredImage) {
        onProgress({
          connectionId: connection.id,
          platformName: 'WordPress',
          status: 'in_progress',
          message: 'Uploading featured image...'
        });

        const filename = `featured-${Date.now()}.jpg`;
        featuredMediaId = await this.uploadImage(
          connection,
          post.featuredImage,
          filename
        );
      }

      // Publish the post
      onProgress({
        connectionId: connection.id,
        platformName: 'WordPress',
        status: 'in_progress',
        message: 'Publishing to WordPress...'
      });

      const postData: any = {
        title: post.title,
        content: htmlContent,
        status: post.status || connection.settings.defaultStatus || 'publish',
        excerpt: post.excerpt || '',
      };

      // Add categories if any
      if (categoryIds.length > 0) {
        postData.categories = categoryIds;
      }

      // Add tags if any
      if (tagIds.length > 0) {
        postData.tags = tagIds;
      }

      // Add featured image if uploaded
      if (featuredMediaId) {
        postData.featured_media = featuredMediaId;
      }

      const response = await fetch(`${siteUrl}/wp-json/wp/v2/posts`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${btoa(`${username}:${appPassword}`)}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(postData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`WordPress API error: ${errorData.message || response.statusText}`);
      }

      const result = await response.json();
      const externalUrl = result.link;

      onProgress({
        connectionId: connection.id,
        platformName: 'WordPress',
        status: 'success',
        message: 'Successfully published to WordPress',
        externalUrl
      });

      return result.id.toString();
    } catch (error) {
      onProgress({
        connectionId: connection.id,
        platformName: 'WordPress',
        status: 'error',
        message: error instanceof Error ? error.message : 'Unknown error occurred'
      });
      throw error;
    }
  }

  /**
   * Update existing WordPress post
   */
  async updateWordPressPost(
    connection: WordPressConnection,
    postId: string,
    post: WordPressPost,
    onProgress: (progress: PublishingProgress) => void
  ): Promise<void> {
    const { siteUrl, username, appPassword } = connection.settings;

    try {
      onProgress({
        connectionId: connection.id,
        platformName: 'WordPress',
        status: 'in_progress',
        message: 'Updating WordPress post...'
      });

      let htmlContent = this.convertMarkdownToHtml(post.content);

      // Inject JSON-LD Schema
      const schema = geoUtils.generateArticleSchema({
        title: post.title,
        summary: post.excerpt || '',
        authorName: 'Content Growth User',
        authorJobTitle: post.authorSettings?.jobTitle,
        authorBio: post.authorSettings?.bio,
        authorSocialLinks: post.authorSettings?.socialLinks ? Object.values(post.authorSettings.socialLinks).filter(Boolean) as string[] : undefined,
      });
      htmlContent += schema;

      // Inject Visible Summary
      if (post.excerpt) {
        htmlContent = geoUtils.generateVisibleSummary(post.excerpt) + htmlContent;
      }

      const categoryIds = post.categories && post.categories.length > 0
        ? await Promise.all(post.categories.map(cat => this.getCategoryId(connection, cat)))
        : [];

      const tagIds = post.tags && post.tags.length > 0
        ? await this.getTagIds(connection, post.tags)
        : [];

      const postData: any = {
        title: post.title,
        content: htmlContent,
        excerpt: post.excerpt || ''
      };

      if (categoryIds.length > 0) {
        postData.categories = categoryIds;
      }

      if (tagIds.length > 0) {
        postData.tags = tagIds;
      }

      const response = await fetch(`${siteUrl}/wp-json/wp/v2/posts/${postId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Basic ${btoa(`${username}:${appPassword}`)}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(postData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`WordPress API error: ${errorData.message || response.statusText}`);
      }

      onProgress({
        connectionId: connection.id,
        platformName: 'WordPress',
        status: 'success',
        message: 'Successfully updated WordPress post'
      });
    } catch (error) {
      onProgress({
        connectionId: connection.id,
        platformName: 'WordPress',
        status: 'error',
        message: error instanceof Error ? error.message : 'Unknown error occurred'
      });
      throw error;
    }
  }

  /**
   * Test WordPress connection
   */
  async testConnection(connection: WordPressConnection): Promise<boolean> {
    try {
      const { siteUrl, username, appPassword } = connection.settings;

      // Try to get current user info
      const response = await fetch(`${siteUrl}/wp-json/wp/v2/users/me`, {
        headers: {
          'Authorization': `Basic ${btoa(`${username}:${appPassword}`)}`
        }
      });

      return response.ok;
    } catch (error) {
      console.error('WordPress connection test failed:', error);
      return false;
    }
  }

  /**
   * Publish article to WordPress.com via OAuth
   * 
   * IMPORTANT: WordPress.com OAuth publishing CANNOT be done directly from the frontend
   * because WordPress.com's REST API does not support CORS (Cross-Origin Resource Sharing).
   * 
   * When the browser tries to make a request to https://public-api.wordpress.com from
   * our frontend domain, the browser blocks it with a CORS error because WordPress.com
   * doesn't include the necessary Access-Control-Allow-Origin headers.
   * 
   * Solution: We proxy the request through our backend API (/api/publishing/publish).
   * The backend can make the request to WordPress.com without CORS restrictions since
   * CORS is a browser security feature, not a server-to-server restriction.
   * 
   * This is different from:
   * - BlogSpot: Google APIs support CORS, can publish from frontend
   * - Medium: Medium API supports CORS, can publish from frontend  
   * - WordPress (self-hosted): User controls their own site, can enable CORS
   */
  async publishToWordPressOAuth(
    connection: any,
    post: WordPressPost,
    onProgress: (progress: PublishingProgress) => void
  ): Promise<string> {
    try {
      onProgress({
        connectionId: connection.id,
        platformName: 'WordPress.com',
        status: 'in_progress',
        message: 'Preparing content...'
      });

      // Prepare article data - backend will convert markdown to HTML
      const postData = {
        title: post.title,
        content: (() => {
          // We need to inject schema into markdown or HTML? 
          // WP.com OAuth usually accepts HTML in content. 
          // But here we are passing raw content? 
          // The comment says "backend will convert markdown to HTML".
          // Ideally we should inject it here before sending to backend?
          // OR we modify the content string to include the raw HTML script tag at the end.
          // Markdown parsers might escape it.
          // Let's assume the backend handles it or we append it delicately.
          // Actually, for consistency, let's append the Schema script block to the markdown.
          // Most markdown parsers allow raw HTML.
          const schema = geoUtils.generateArticleSchema({
            title: post.title,
            summary: post.excerpt || '',
            authorName: 'Content Growth User',
            authorJobTitle: post.authorSettings?.jobTitle,
            authorBio: post.authorSettings?.bio,
            authorSocialLinks: post.authorSettings?.socialLinks ? Object.values(post.authorSettings.socialLinks).filter(Boolean) as string[] : undefined,
          });

          let enhancedContent = post.content;
          if (post.excerpt) {
            // We can't easily inject HTML logic into markdown for visual summary without potentially breaking things
            // So for OAuth we might skip visual summary or append it as text.
            // Let's append the schema at the end.
            enhancedContent += '\n\n' + schema;
          } else {
            enhancedContent += '\n\n' + schema;
          }
          return enhancedContent;
        })(),
        status: post.status || connection.settings.defaultStatus || 'publish',
        excerpt: post.excerpt || '',
        tags: post.tags || [],
        category: post.categories?.[0] || undefined
      };

      onProgress({
        connectionId: connection.id,
        platformName: 'WordPress.com',
        status: 'in_progress',
        message: 'Publishing to WordPress.com...'
      });

      // Call backend API to publish (backend handles WordPress.com API call)
      const response = await apiClient.post('/api/publishing/publish', {
        connectionId: connection.id,
        article: postData
      });

      const result = response.data;
      const externalUrl = result.externalUrl;
      const externalId = result.externalId;

      onProgress({
        connectionId: connection.id,
        platformName: 'WordPress.com',
        status: 'success',
        message: `Successfully published to WordPress.com (ID: ${externalId})`,
        externalUrl
      });

      return externalId;
    } catch (error) {
      onProgress({
        connectionId: connection.id,
        platformName: 'WordPress.com',
        status: 'error',
        message: error instanceof Error ? error.message : 'Unknown error occurred'
      });
      throw error;
    }
  }
}

export const wordPressPublishingService = new WordPressPublishingService();
